import React from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

interface SavingsTrendChartProps {
  data: Array<{
    month: string;
    estimated_savings: number;
    released_savings: number;
    cumulative_estimated: number;
    cumulative_released: number;
  }>;
  loading?: boolean;
}

const SavingsTrendChart: React.FC<SavingsTrendChartProps> = ({ data, loading = false }) => {
  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Savings Trend Over Time</h3>
        <div className="h-64 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Savings Trend Over Time</h3>
        <div className="h-64 flex items-center justify-center text-gray-500">
          No savings data available
        </div>
      </div>
    );
  }

  const formatCurrency = (value: number) => {
    if (value >= 1000000000) {
      return `€${(value / 1000000000).toFixed(1)}B`;
    } else if (value >= 1000000) {
      return `€${(value / 1000000).toFixed(1)}M`;
    } else if (value >= 1000) {
      return `€${(value / 1000).toFixed(1)}K`;
    }
    return `€${value.toFixed(0)}`;
  };

  const formatMonth = (month: string) => {
    const date = new Date(month);
    return date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
  };

  const totalEstimated = data.reduce((sum, item) => sum + item.estimated_savings, 0);
  const totalReleased = data.reduce((sum, item) => sum + item.released_savings, 0);
  const overallRealizationRate = totalReleased / totalEstimated * 100;

  return (
    <div className="bg-white rounded-lg shadow-sm border p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Savings Trend Over Time</h3>
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="month" 
              tick={{ fontSize: 12 }}
              tickFormatter={formatMonth}
            />
            <YAxis 
              tick={{ fontSize: 12 }}
              tickFormatter={formatCurrency}
              domain={[0, 'dataMax + 10000']}
            />
            <Tooltip 
              formatter={(value: number, name: string) => [
                formatCurrency(value),
                name === 'estimated_savings' ? 'Estimated Savings' :
                name === 'released_savings' ? 'Released Savings' :
                name === 'cumulative_estimated' ? 'Cumulative Estimated' :
                'Cumulative Released'
              ]}
              labelFormatter={(label) => `Month: ${formatMonth(label)}`}
            />
            <Legend />
            <Area
              type="monotone"
              dataKey="estimated_savings"
              stackId="1"
              stroke="#3b82f6"
              fill="#3b82f6"
              fillOpacity={0.6}
              name="Estimated Savings"
            />
            <Area
              type="monotone"
              dataKey="released_savings"
              stackId="1"
              stroke="#10b981"
              fill="#10b981"
              fillOpacity={0.6}
              name="Released Savings"
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
      <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-blue-50 p-3 rounded-lg">
          <h4 className="font-medium text-blue-900 mb-1">Total Estimated</h4>
          <p className="text-2xl font-bold text-blue-600">{formatCurrency(totalEstimated)}</p>
          <p className="text-sm text-blue-700">Over {data.length} months</p>
        </div>
        <div className="bg-green-50 p-3 rounded-lg">
          <h4 className="font-medium text-green-900 mb-1">Total Released</h4>
          <p className="text-2xl font-bold text-green-600">{formatCurrency(totalReleased)}</p>
          <p className="text-sm text-green-700">Realized savings</p>
        </div>
        <div className="bg-purple-50 p-3 rounded-lg">
          <h4 className="font-medium text-purple-900 mb-1">Realization Rate</h4>
          <p className="text-2xl font-bold text-purple-600">{overallRealizationRate.toFixed(1)}%</p>
          <p className="text-sm text-purple-700">Overall performance</p>
        </div>
      </div>
    </div>
  );
};

export default SavingsTrendChart;